<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 12px;">
      <h2 class="mb-0">Relatório de Estoque</h2>
      <v-btn outlined color="primary" @click="$router.push('/relatorios#financeiro')">
        <v-icon left>mdi-arrow-left</v-icon>
        Voltar ao menu
      </v-btn>
    </div>

    <div class="d-flex flex-wrap align-center mt-4 mb-4" style="gap: 12px;">
      <v-text-field v-model="filtros.dataInicio" type="date" label="Data inicial" solo hide-details clearable class="c-filtro" />
      <v-text-field v-model="filtros.dataFim" type="date" label="Data final" solo hide-details clearable class="c-filtro" />

      <v-text-field v-model="filtros.codigoItem" label="Código do Item" solo hide-details clearable class="c-filtro" />
      <v-text-field v-model="filtros.descricaoItem" label="Descrição do Item" solo hide-details clearable class="c-filtro" />

      <v-btn color="primary" :loading="loading" @click="buscarDados">
        <v-icon left>mdi-refresh</v-icon>
        Recarregar
      </v-btn>

      <v-menu>
        <template v-slot:activator="{ on }">
          <v-btn color="primary" outlined v-on="on" :disabled="loading || dadosFiltrados.length === 0">
            <v-icon left>mdi-file-export-outline</v-icon>
            Exportar
          </v-btn>
        </template>
        <v-list>
          <v-list-item link @click="gerarExcel"><v-list-item-title>Excel</v-list-item-title></v-list-item>
          <v-list-item link @click="gerarPdf"><v-list-item-title>PDF</v-list-item-title></v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div ref="areaPdf">
      <v-data-table 
        :headers="headers" 
        :items="dadosFiltrados" 
        :loading="loading" 
        item-key="_id" 
        :footer-props="{ 'items-per-page-text': 'Linhas por página:', 'page-text': '{0}-{1} de {2}' }"
        class="elevation-1"
      >
        <template v-slot:item.data="{ item }">{{ formatarDataPtBr(item.data) }}</template>
        <template v-slot:item.qtdInicial="{ item }">{{ formatarNumero(item.qtdInicial) }}</template>
        <template v-slot:item.qtdEntrada="{ item }">{{ formatarNumero(item.qtdEntrada) }}</template>
        <template v-slot:item.qtdSaida="{ item }">{{ formatarNumero(item.qtdSaida) }}</template>
        <template v-slot:item.qtdFinal="{ item }">{{ formatarNumero(item.qtdFinal) }}</template>
        <template v-slot:item.vlrUnitario="{ item }">{{ formatarMoedaPtBr(item.vlrUnitario) }}</template>
        <template v-slot:item.precoMedio="{ item }">{{ formatarMoedaPtBr(item.precoMedio) }}</template>
        <template v-slot:item.vlrTotal="{ item }">{{ formatarMoedaPtBr(item.vlrTotal) }}</template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import relatorioEstoqueService from "@/views/Relatorios/services/relatorioEstoqueService";
import { exportarExcel, exportarPdfTabela, formatarDataPtBr, formatarMoedaPtBr, normalizarTexto } from "@/views/Relatorios/base/relatorioBaseUtils";

const formatarNumero = (valor) => {
  const numero = Number(valor || 0);
  if (!Number.isFinite(numero)) return "0";
  return String(numero);
};

export default {
  name: "RelatorioEstoqueView",
  data() {
    return {
      loading: false,
      dados: [],
      filtros: { dataInicio: "", dataFim: "", codigoItem: "", descricaoItem: "" },
      headers: [
        { text: "Data", value: "data" },
        { text: "Código do Item", value: "codigoItem" },
        { text: "Descrição", value: "descricao" },
        { text: "Qtd", value: "qtdInicial" },
        { text: "Entrada", value: "qtdEntrada" },
        { text: "Saída", value: "qtdSaida" },
        { text: "Estoque Final", value: "qtdFinal" },
        { text: "Valor Unitário", value: "vlrUnitario" },
        { text: "Preço Médio", value: "precoMedio" },
        { text: "Valor Total", value: "vlrTotal" },
        { text: "NCM", value: "ncm" }
      ]
    };
  },
  computed: {
    dadosFiltrados() {
      const codigoItem = normalizarTexto(this.filtros.codigoItem).toLowerCase();
      const descricaoItem = normalizarTexto(this.filtros.descricaoItem).toLowerCase();

      return this.dados.filter((item) => {
        const codigoOk = !codigoItem || normalizarTexto(item.codigoItem).toLowerCase().includes(codigoItem);
        const descricaoOk = !descricaoItem || normalizarTexto(item.descricao).toLowerCase().includes(descricaoItem);
        return codigoOk && descricaoOk;
      });
    }
  },
  watch: {
    'filtros.dataInicio'() { this.buscarDados(); },
    'filtros.dataFim'() { this.buscarDados(); }
  },
  methods: {
    formatarDataPtBr,
    formatarMoedaPtBr,
    formatarNumero,
    async buscarDados() {
      this.loading = true;
      try {
        const lista = await relatorioEstoqueService.listarRelatorioEstoque(this.filtros);
        this.dados = Array.isArray(lista) ? lista : [];
      } finally {
        this.loading = false;
      }
    },
    gerarExcel() {
      exportarExcel(this.dadosFiltrados, "Relatório de Estoque.xlsx");
    },
    async gerarPdf() {
      if (!this.dadosFiltrados.length) return;

      await exportarPdfTabela({
        headers: this.headers,
        rows: this.dadosFiltrados,
        nomeArquivo: "Relatório de Estoque.pdf",
        tituloRelatorio: "Relatório de Estoque",
        formatadoresPorCampo: {
          data: (valor) => this.formatarDataPtBr(valor),
          qtdInicial: (valor) => this.formatarNumero(valor),
          qtdEntrada: (valor) => this.formatarNumero(valor),
          qtdSaida: (valor) => this.formatarNumero(valor),
          qtdFinal: (valor) => this.formatarNumero(valor),
          vlrUnitario: (valor) => this.formatarMoedaPtBr(valor),
          precoMedio: (valor) => this.formatarMoedaPtBr(valor),
          vlrTotal: (valor) => this.formatarMoedaPtBr(valor)
        }
      });
    }
  },
  mounted() {
    this.buscarDados();
  }
};
</script>

<style scoped>
.c-filtro {
  max-width: 220px !important;
}
</style>
